import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Clear, ExpandMore } from "@mui/icons-material";

const ObjectiveLevelViewer = ({
  level,
  index,
  onLevelChange,
  onDelete = null,
  readOnly = false,
}) => {
  return (
    <Card variant="background" sx={{ mb: 2 }}>
      <CardContent>
        <Typography>Level {index + 1}</Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Criteria"
            value={level.levelCriteria}
            onChange={(e) =>
              onLevelChange({
                index,
                field: "levelCriteria",
                value: e.target.value,
              })
            }
            size="small"
            fullWidth
            disabled={readOnly}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Min Mark"
            type="number"
            value={level.minMark}
            onChange={(e) =>
              onLevelChange({
                index,
                field: "minMark",
                value: Number(e.target.value),
              })
            }
            size="small"
            disabled={readOnly}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Max Mark"
            type="number"
            value={level.maxMark}
            onChange={(e) =>
              onLevelChange({
                index,
                field: "maxMark",
                value: Number(e.target.value),
              })
            }
            size="small"
            disabled={readOnly}
          />
          {onDelete && !readOnly && (
            <IconButton sx={{ border: "none" }} onClick={() => onDelete(index)}>
              <Clear />
            </IconButton>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const AssessmentObjectiveAccordion = ({
  objective,
  onObjectiveChange,
  onObjectiveRemove,
  readOnly = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const maxMarks = useMemo(() => {
    if (!objective?.levels?.length) return 0;
    return Math.max(...objective.levels.map((l) => Number(l.maxMark ?? 0)));
  }, [objective.levels]);

  const handleLevelChange = ({ index, field, value }) => {
    const levels = [...(objective.levels || [])];
    let currentLevel = { ...levels[index] };

    if (field === "maxMark") {
      const min = Number(currentLevel.minMark ?? 0);
      if (value >= min) {
        currentLevel.maxMark = value;

        if (index + 1 < levels.length) {
          const nextLevel = { ...levels[index + 1] };
          if (value < nextLevel.maxMark) {
            nextLevel.minMark = value + 1;
            levels[index + 1] = nextLevel;
          }
        }
      }
    }

    if (field === "minMark") {
      if (value <= currentLevel.maxMark) {
        currentLevel.minMark = value;

        if (index > 0) {
          const prevLevel = { ...levels[index - 1] };
          const proposedPreviousMax = value - 1;
          if (proposedPreviousMax >= prevLevel.minMark) {
            prevLevel.maxMark = proposedPreviousMax;
            levels[index - 1] = prevLevel;
          }
        }
      }
    }

    if (field === "levelCriteria") {
      currentLevel.levelCriteria = value;
    }

    levels[index] = currentLevel;
    onObjectiveChange({ ...objective, levels });
  };

  const handleDeleteLevel = (index) => {
    let levels = [...(objective.levels || [])];

    // Remove the level at given index
    levels.splice(index, 1);

    // Adjust ranges
    if (levels.length > 0) {
      // If we deleted a middle level, fix the gap
      if (index > 0 && index < levels.length) {
        const prevLevel = { ...levels[index - 1] };
        const nextLevel = { ...levels[index] };

        // ensure continuity: prev.max = next.min - 1
        if (nextLevel.minMark !== undefined) {
          prevLevel.maxMark = nextLevel.minMark - 1;
          levels[index - 1] = prevLevel;
        }
      }

      // If we deleted the last level, no nextLevel — just ensure prev is valid
      if (index === levels.length && index > 0) {
        const prevLevel = { ...levels[index - 1] };
        if (prevLevel.maxMark < prevLevel.minMark) {
          prevLevel.maxMark = prevLevel.minMark; // clamp if broken
          levels[index - 1] = prevLevel;
        }
      }

      // If we deleted the first level, shift the next one’s minMark to 0
      if (index === 0 && levels.length > 0) {
        const firstLevel = { ...levels[0] };
        firstLevel.minMark = 0;
        levels[0] = firstLevel;
      }
    }

    onObjectiveChange({ ...objective, levels });
  };
  const handleAddLevel = () => {
    let levels = [...(objective.levels || [])];

    // If no levels yet, start fresh
    if (levels.length === 0) {
      levels.push({
        id: Date.now(), // temp unique id
        levelCriteria: "",
        minMark: 0,
        maxMark: 0,
      });
    } else {
      const lastLevel = levels[levels.length - 1];
      const newMin = lastLevel.maxMark + 1;

      levels.push({
        id: Date.now(), // temp unique id
        levelCriteria: "",
        minMark: newMin,
        maxMark: newMin, // default same as min, teacher can expand it
      });
    }

    onObjectiveChange({ ...objective, levels });
  };

  const levelsIncomplete = objective.levels.some(
    (level) => !level.levelCriteria || level.levelCriteria === "",
  );
  return (
    <Accordion expanded={expanded} variant={"outlined"}>
      <AccordionSummary onClick={() => setExpanded(!expanded)}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          {/* Left side */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="subtitle2"
              fontWeight="medium"
              sx={{ ml: 1, color: theme.palette.primary.main }}
            >
              Show {objective.assessmentObjective?.identifier}
            </Typography>
            <ExpandMore />
            {objective.assessmentObjective?.subject?.name && (
              <Chip label={objective.assessmentObjective.subject.name} />
            )}
          </Stack>

          {/* Right side */}
          <Stack direction={"row"} spacing={2} alignItems={"center"}>
            <Typography variant="subtitle2" fontWeight="medium">
              Max marks {maxMarks}
            </Typography>
            {!readOnly && (
              <Button
                component="span"
                variant={"card-action"}
                onClick={() => onObjectiveRemove(objective)}
              >
                Remove
              </Button>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant={"subtitle2"} fontWeight="regular" sx={{ mb: 2 }}>
          {objective.assessmentObjective?.focusArea}
        </Typography>
        <TextField
          label="Additional instructions"
          helperText={
            readOnly ? "" : "For example focusing on a specific part of the AO"
          }
          value={objective.additionalInstructions}
          onChange={(e) =>
            onObjectiveChange({
              ...objective,
              additionalInstructions: e.target.value,
            })
          }
          size="small"
          fullWidth
          disabled={readOnly}
          inputProps={{ min: 0 }}
          sx={{ mb: 2 }}
        />
        {objective.levels.map((level, index) => (
          <ObjectiveLevelViewer
            level={level}
            onLevelChange={handleLevelChange}
            index={index}
            key={level.id}
            onDelete={handleDeleteLevel}
            readOnly={readOnly}
          />
        ))}
        {!readOnly && (
          <Button
            variant="contained"
            onClick={handleAddLevel}
            disabled={levelsIncomplete}
          >
            + Add Level
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export { AssessmentObjectiveAccordion, ObjectiveLevelViewer };
